import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { Tag } from '@givebutter/primitives/tag';
import { fetchAccountMembers } from 'api/account';
import { ShareReportFormValues } from 'assets/js/pages/reports/types';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import Fuse from 'fuse.js';
import { isEmail } from 'helpers/regex';
import { uniq } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMember } from 'types/domain';

const MEMBERS_PARAMS = {
    page: 1,
    per_page: 9999,
};

const ShareReportSendToField = () => {
    const { accountId } = useCurrentAccount();
    const [emailInput, setEmailInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const [searchResults, setSearchResults] = useState<AccountMember[]>([]);
    const searchInstance = useMemo(() => new Fuse<string>([], { threshold: 0.3 }), []);

    const { control, path, watch, setValue, getValues } =
        Form.useFormContext<ShareReportFormValues>();
    const emails = watch('emails');

    const { data: membersData } = useSuspenseQuery({
        queryKey: accountQueryKeys.membersWithFilters(accountId, MEMBERS_PARAMS),
        queryFn: () => fetchAccountMembers(accountId, MEMBERS_PARAMS),
    });
    const members = membersData.data;
    const membersExcludingSelected = members.filter(excludeSelectedEmails(emails));
    const searchResultsExcludingSelected = searchResults.filter(excludeSelectedEmails(emails));
    const visibleMemberOptions =
        emailInput.length > 0 ? searchResultsExcludingSelected : membersExcludingSelected;
    const nonMemberEmails = emails.filter(v => !members.find(m => m.email === v));
    const hiddenMemberOptions = uniq([...nonMemberEmails, ...emails]);

    const onSelectEmail = () => {
        setEmailInput('');
        inputRef.current?.focus();
    };

    const appendPossibleEmail = () => {
        const sanitizedEmail = sanitizeEmail(emailInput);
        if (isValidEmail(sanitizedEmail)) {
            setValue('emails', uniq(getValues('emails').concat(sanitizedEmail)));
            onSelectEmail();
        }
    };

    const handleSearch = useCallback(
        (value: string) => {
            setEmailInput(value);
            const results = searchInstance.search(value);
            setSearchResults(members.filter(m => results.find(r => r.item === m.email)));
        },
        [members, searchInstance],
    );

    useEffect(() => {
        searchInstance.setCollection(members.map(m => m.email));
    }, [members, searchInstance]);

    return (
        <div className={'flex flex-col gap-2'}>
            <Form.Container>
                <Form.Label name={path('emails')}>
                    Send to
                    <Form.LabelAsterisk />
                </Form.Label>
                <Form.Combobox control={control} name="emails">
                    <Combobox.Trigger aria-label="Enter email addresses">
                        <Combobox.Tags placeholder="Enter email addresses" />
                    </Combobox.Trigger>
                    <Combobox.Popover>
                        <Combobox.Search
                            ref={inputRef}
                            value={emailInput}
                            onChangeValue={handleSearch}
                            placeholder="Enter email addresses separated with a comma"
                            onBlur={() => appendPossibleEmail()}
                            onKeyDown={e => {
                                const isEnter = e.key === 'Enter';

                                if (isEnter) {
                                    e.preventDefault();
                                }

                                if (e.key === 'Tab' || e.key === ',' || isEnter) {
                                    if (isValidEmail(sanitizeEmail(emailInput))) {
                                        e.preventDefault();
                                    }
                                    appendPossibleEmail();
                                }
                            }}
                        />
                        <Combobox.Items>
                            {visibleMemberOptions.map(member => (
                                <Combobox.Item
                                    value={member.email}
                                    key={member.id}
                                    onSelectItem={onSelectEmail}
                                >
                                    <div className="flex flex-col text-sm">
                                        <p className="font-medium">
                                            {member.first_name} {member.last_name}
                                        </p>
                                        <p className={'font-normal text-gray-400'}>
                                            <Combobox.ItemText>{member.email}</Combobox.ItemText>
                                        </p>
                                    </div>
                                </Combobox.Item>
                            ))}
                            {hiddenMemberOptions.map(email => (
                                <Combobox.Item
                                    value={email}
                                    key={email}
                                    className="hidden"
                                    tabIndex={-1}
                                >
                                    <Combobox.ItemText>{email}</Combobox.ItemText>
                                </Combobox.Item>
                            ))}
                        </Combobox.Items>
                    </Combobox.Popover>
                </Form.Combobox>
                <Form.Error name={path('emails')} />
            </Form.Container>

            <div className="flex items-center gap-1.5">
                {membersExcludingSelected.slice(0, 4).map(member => (
                    <Button
                        variant="none"
                        className="font-medium"
                        key={member.id}
                        onClick={() =>
                            setValue('emails', uniq(getValues('emails').concat(member.email)))
                        }
                    >
                        <Tag size="sm" className="rounded-full">
                            + {member.first_name} {member.last_name}
                        </Tag>
                    </Button>
                ))}
            </div>
        </div>
    );
};

const sanitizeEmail = (email: string) => email.trim();

const excludeSelectedEmails = (emails: string[]) => (member: AccountMember) =>
    !emails.find(m => m === member.email);

const isValidEmail = (email: string) => isEmail.test(email);

export { ShareReportSendToField };
